import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useFieldTypes, usePaymentFields } from "@features/cx/settings/hooks";

interface CustomField {
  key: string;
  label: string;
  fieldType: string;
}

interface PaymentMode {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  transactionFee?: number;
  remark?: string;
  minAmount?: number;
  maxAmount?: number;
  customFields?: CustomField[];
}

interface PaymentModeViewProps {
  isOpen: boolean;
  onClose: () => void;
  viewingMode: PaymentMode | null;
}

const fieldTypeOptions = [
  { value: "text", label: "Text Field" },
  { value: "number", label: "Number Field" },
  { value: "date", label: "Date Field" },
];

export function PaymentModeView({
  isOpen,
  onClose,
  viewingMode,
}: PaymentModeViewProps) {
  const { data: fieldTypes } = useFieldTypes();
  const { data: paymentFields } = usePaymentFields();
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    transactionFee: 0,
    remark: "",
    minAmount: 0,
    maxAmount: 0,
    customFields: [] as CustomField[],
  });

  // Helper function to get default fields from API response
  const getDefaultFields = useMemo(() => {
    if (paymentFields && Array.isArray(paymentFields)) {
      return paymentFields.map((field) => ({
        key: field.key || Date.now().toString(),
        label: field.label,
        fieldType: field.fieldType,
      }));
    }
    return [];
  }, [paymentFields]);

  useEffect(() => {
    if (isOpen && viewingMode) {
      setFormData({
        name: viewingMode.name,
        type: viewingMode.type,
        transactionFee: viewingMode.transactionFee || 0,
        remark: viewingMode.remark || "",
        minAmount: viewingMode.minAmount || 0,
        maxAmount: viewingMode.maxAmount || 0,
        customFields:
          viewingMode.customFields?.map((field) => ({
            key: field.key,
            label: field.label,
            fieldType: field.fieldType,
          })) || [],
      });
    }
  }, [isOpen, viewingMode, getDefaultFields]);

  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Mode Name",
      placeholder: "Enter mode name",
      required: true,
      className: "col-span-2 mt-3",
      disabled: true,
      classes: {
        input:
          "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed",
      },
    },
    {
      name: "remark",
      type: "textarea",
      label: "Description",
      disabled: true,
      placeholder: "Enter mode description",
      rows: 3,
      className: "col-span-2 mt-3",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  const handleFieldChange = (fieldName: string, value: any) => {
    // No-op for view mode
  };

  const handleSubmit = () => {
    // No-op for view mode
  };

  const handleClose = () => {
    onClose();
  };

  // Get available field types - use API data if available, fallback to hardcoded options
  const availableFieldTypes = useMemo(() => {
    if (fieldTypes && Array.isArray(fieldTypes)) {
      return fieldTypes.map((type) => ({
        value: type.value.toLowerCase(),
        label: type.key || type.value,
      }));
    }
    return fieldTypeOptions;
  }, [fieldTypes]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Payment Mode</DialogTitle>
          <DialogDescription>
            Payment mode details and configuration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information Form */}
          <div className="space-y-4">
            <h4 className="font-medium">Basic Information</h4>
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleClose}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                spacing: "comfortable",
                classes: {
                  form: "space-y-6",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical bg-gray-100 cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  container: "space-y-1",
                },
              }}
            />
          </div>

          {/* Configured Fields - View Only */}
          {formData.customFields.length > 0 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <h5 className="font-medium">Configured Fields</h5>
                <div className="space-y-2">
                  {formData.customFields.map((field) => (
                    <div
                      key={field.key}
                      className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <span className="font-medium">{field.label}</span>
                          <span className="ml-2 text-sm text-muted-foreground">
                            (
                            {
                              availableFieldTypes.find(
                                (opt) => opt.value === field.fieldType
                              )?.label
                            }
                            )
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Custom Form Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-gray-200 mt-8">
            <Button
              variant="outline"
              onClick={handleClose}
              className="px-4 py-2 rounded-md font-medium transition-colors"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
